/**
 * What the figures on the start screen and the detail screen say.
 *
 * Three questions, and they are not the same one:
 *
 *   Where the mistakes come from — the share per kind, out of everything he
 *   wrote wrongly. Says what to practise.
 *
 *   How securely it sits — the error rate when asked. Says what he can
 *   already do. A kind can be the most frequent one and sit perfectly well;
 *   without this the first figure would go on calling it the biggest problem.
 *
 *   How it develops — the same rate over four weeks.
 *
 * The first is worked out from what is already stored; the other two come
 * from `answers.ts`, which had to be built for them.
 */

import type { Mistake, Word } from '../types.js'
import { kindFromRule } from './exercises.js'
import { ratesByKind, weeklyRates, type Answer } from './answers.js'

/**
 * What a kind of mistake is called on screen.
 *
 * The tone comes from the practised list (`spell/groups.ts`): what a child
 * would say, not what a grammar book would. "Doppelte Mitlaute" and not
 * "Doppelkonsonanz".
 */
export const KIND_LABELS: Record<string, string> = {
  'gross-klein': 'Groß und klein',
  'nach-gehoer': 'Nach Gehör',
  'doppelkonsonant': 'Doppelte Mitlaute',
  's-ss-ß': 's, ss oder ß',
  'dehnungs-h': 'Dehnungs-h',
  'endung': 'Endungen',
  'ie-i': 'ie oder i',
  'umlaut': 'Umlaute',
  'd-t': 'd oder t',
  'verwechselt': 'Verwechselt',
  'getrennt-zusammen': 'Getrennt oder zusammen',
}

export const labelFor = (kind: string): string => KIND_LABELS[kind] ?? kind

/**
 * The colours of the kinds.
 *
 * One colour per kind, the same one everywhere: in the ring, in the trend and
 * in the bars. A colour that meant a mistake kind on one screen and something
 * else on another would be worse than no colour at all.
 *
 * Six of them, and there are eleven kinds — the rest goes into "Anderes", see
 * `MAX_SLICES`. Six is what can be told apart at a glance in a ring this
 * size; eleven would be confetti.
 */
export const KIND_COLORS = [
  '#78a9ff', '#5ec8c0', '#c49bf0', '#f0a848', '#ff9db4', '#8fd17a',
] as const

/** The colour of the pooled rest. Grey, because it is not one thing. */
export const REST_COLOR = '#8195b0'

/** How many kinds get a slice of their own. */
const MAX_SLICES = 6

/** The key of the pooled rest. */
export const REST = 'anderes'

/** One kind, as the screens draw it. */
export interface Slice {
  kind: string
  label: string
  color: string
  /** How many mistakes of this kind there are. */
  count: number
  /** Its share of all mistakes, 0 to 100. Rounded so the sum is exactly 100. */
  share: number
}

/**
 * Where the mistakes come from: the share per kind.
 *
 * Counted are ALL of them, the practised ones included. The question is what
 * he gets wrong, not what is still to be done — a kind that has been fully
 * practised is still the kind he had the most trouble with.
 */
export function distribution(mistakes: Mistake[], words: Word[]): Slice[] {
  const by = new Map<string, number>()
  const add = (kind: string): void => {
    if (!kind) return
    by.set(kind, (by.get(kind) ?? 0) + 1)
  }

  /**
   * What the model said about a pair of spellings.
   *
   * It reads the whole page and names the kind outright; on the typed side
   * there is only a LanguageTool rule, and `GERMAN_SPELLER_RULE` sits under
   * every second word mistake. Where both know the same word, the model is
   * the better witness.
   *
   * Since a photographed page is now a text as well, the same word really can
   * arrive twice: once found by the model, once corrected on the paper.
   */
  const byModel = new Map<string, string>()
  for (const m of mistakes) {
    if (m.kind) byModel.set(pairKey(m.wrong, m.right), m.kind)
  }

  for (const m of mistakes) add(m.kind)
  for (const w of words) {
    const pair = pairKey(w.wrong, w.right ?? '')
    // Counted once, not twice: correcting on the paper what the camera found
    // is one mistake that was dealt with, not two that happened.
    if (byModel.has(pair)) continue
    // A corrected word carries a LanguageTool rule, not a kind — the same
    // translation the exercises use, so both screens group the same way.
    add(kindFromRule(w.rule ?? '', w.wrong, w.right ?? ''))
  }

  const sorted = [...by.entries()].sort((a, b) => b[1] - a[1])
  const total = sorted.reduce((n, [, c]) => n + c, 0)
  if (!total) return []

  const head = sorted.slice(0, MAX_SLICES)
  const tail = sorted.slice(MAX_SLICES)
  const rest = tail.reduce((n, [, c]) => n + c, 0)

  const rows: { kind: string; count: number; color: string }[] = head.map(
    ([kind, count], i) => ({ kind, count, color: KIND_COLORS[i]! })
  )
  if (rest) rows.push({ kind: REST, count: rest, color: REST_COLOR })

  return withShares(rows, total)
}

/** One pair of spellings, as a key. Case does not make two mistakes of one. */
const pairKey = (wrong: string, right: string): string =>
  `${wrong.toLowerCase()}→${right.toLowerCase()}`

/**
 * Turns counts into percentages that really add up to 100.
 *
 * Rounding each one on its own gives 99 or 101, and a ring whose labels say
 * 101 % is wrong in the one place where a reader checks. The largest
 * remainder gets the odd point — the usual way, and the smallest lie.
 */
function withShares(
  rows: { kind: string; count: number; color: string }[],
  total: number
): Slice[] {
  const exact = rows.map((r) => (r.count / total) * 100)
  const floors = exact.map(Math.floor)
  let left = 100 - floors.reduce((a, b) => a + b, 0)

  // Hand out the remaining points to the largest fractional parts.
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac)
  const shares = [...floors]
  for (const { i } of order) {
    if (left <= 0) break
    shares[i] = shares[i]! + 1
    left -= 1
  }

  return rows.map((r, i) => ({
    kind: r.kind,
    label: r.kind === REST ? 'Anderes' : labelFor(r.kind),
    color: r.color,
    count: r.count,
    share: shares[i]!,
  }))
}

/**
 * How well each kind sits, in the order of the distribution.
 *
 * The order comes from `slices` so that a kind keeps its place and its colour
 * across the whole screen. A kind that has never been asked yields `null`,
 * not a zero: zero would read as "cannot do it at all", when in truth it was
 * never asked.
 */
export function rates(
  slices: Slice[],
  list?: Answer[]
): { kind: string; label: string; color: string; mastery: number | null }[] {
  const by = new Map(ratesByKind(0, Infinity, list).map((r) => [r.kind, r]))
  return slices.map((s) => ({
    kind: s.kind,
    label: s.label,
    color: s.color,
    // How well it sits, not how often it went wrong. A bar that grows as one
    // gets better says something else than one that fills up with failure,
    // even at identical numbers.
    mastery: by.get(s.kind)?.mastery ?? null,
  }))
}

/**
 * The course of the error rate, in the order of the distribution.
 *
 * Only the kinds that were really asked: a line at the bottom for a kind
 * never practised would claim it always sat.
 */
export function trend(
  slices: Slice[],
  weeks = 4,
  now = Date.now(),
  list?: Answer[]
): { kind: string; label: string; color: string; points: (number | null)[] }[] {
  const by = weeklyRates(weeks, now, list)
  return slices
    .map((s) => ({
      kind: s.kind,
      label: s.label,
      color: s.color,
      points: by.get(s.kind) ?? Array<number | null>(weeks).fill(null),
    }))
    .filter((r) => r.points.some((p) => p != null))
}
